import { useEffect, useRef } from "react";

/** The ride-along that tells a first-time visitor what Teams is. Each step lights up one part of the room (the room's
 *  `data-tour` attribute drives the glow in world.css) and says, in plain words, what it is. */
export const TOUR = [
  { spot: "", title: "Welcome to your team's room", text: "Behind this room, a team of AI agents works on your computer. You watch them here and steer them from your phone." },
  { spot: "lead", title: "Dot is the main AI", text: "Dot doesn't do the work itself. Tell Dot a goal and Dot splits it into steps and hands them to the agents." },
  { spot: "board", title: "The plan", text: "Dot's steps appear on this board and tick off as agents finish them. Tap it to open the chat." },
  { spot: "desks", title: "Every desk is an agent", text: "Green means working, amber means it needs you, a tick means done. Tap a desk to see what it is doing, its terminal and what it changed." },
  { spot: "desks", title: "Hand-overs", text: "When one agent passes work to another, an envelope flies between their desks. Tap it in the chat to read the message." },
  { spot: "dock", title: "Ask Dot anything", text: "Agents work without asking before each command, so give clear goals. You can stop the team any time with Stop the team." },
] as const;

export function TeamTour({ step, onStep, onDone }: { step: number; onStep: (n: number) => void; onDone: () => void }) {
  const next = useRef<HTMLButtonElement>(null);
  const s = TOUR[step]!;
  const last = step === TOUR.length - 1;
  useEffect(() => {
    next.current?.focus({ preventScroll: true });
    const on = (e: KeyboardEvent) => e.key === "Escape" && onDone();
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [step, onDone]);
  return (
    <div className="tw-tour" role="dialog" aria-label="Tour of Teams" data-testid="team-tour">
      <div className="k">{step + 1} OF {TOUR.length}</div>
      <h3>{s.title}</h3>
      <p>{s.text}</p>
      <div className="row">
        <button className="skip" onClick={onDone}>{last ? "Close" : "Skip"}</button>
        <span className="dots" aria-hidden>{TOUR.map((_, i) => <i key={i} className={i === step ? "on" : ""} />)}</span>
        <button ref={next} className="go" onClick={() => (last ? onDone() : onStep(step + 1))}>{last ? "Got it" : "Next"}</button>
      </div>
    </div>
  );
}
